import React, {useEffect, useRef, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import {AlertTriangleIcon, CalendarIcon} from '../components/icons';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

/**
 * Возврат из Google OAuth при подключении календаря (CTHS-174, FR-21).
 *
 * <p><b>Почему эта страница вообще существует.</b> Backend-эндпоинт обмена кода
 * (`GET /api/calendar/oauth/callback`) требует JWT — иначе он не знал бы, чьё подключение
 * сохранять, и любой мог бы привязать календарь к чужому аккаунту. Браузерный редирект от
 * Google заголовок авторизации не несёт, поэтому `redirect_uri` указывает сюда: страница
 * забирает `code` из адреса и отправляет его на бэкенд уже от имени пользователя.</p>
 *
 * <p>После обмена — возврат в Privacy Center: подключение начиналось там, туда же и
 * возвращаемся, а не оставляем человека на технической странице.</p>
 */
const PRIVACY_ROUTE = '/settings/profile/privacy';

const CalendarOAuthCallbackPage: React.FC = () => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    // React 18 в dev монтирует эффекты дважды — код одноразовый, второй обмен вернёт 400.
    const exchanged = useRef(false);

    useDocumentTitle('Подключение Google Calendar');

    useEffect(() => {
        if (exchanged.current) {
            return;
        }
        exchanged.current = true;

        const code = params.get('code');
        const denied = params.get('error');
        if (denied || !code) {
            setError(denied
                ? 'Доступ к Google Calendar не предоставлен.'
                : 'Google не вернул код подтверждения.');
            return;
        }

        (async () => {
            try {
                const api = await createAxiosInstance();
                await api.get('/calendar/oauth/callback', {params: {code}});
                navigate(PRIVACY_ROUTE, {replace: true});
            } catch {
                setError('Не удалось завершить подключение. Попробуйте ещё раз.');
            }
        })();
    }, [params, navigate]);

    return (
        <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="calendar-callback">
            {error ? (
                <>
                    <AlertTriangleIcon size={28} className="mx-auto text-amber-500"/>
                    <h1 className="mt-3 text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Подключение не завершено
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300" data-testid="calendar-callback-error">
                        {error}
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate(PRIVACY_ROUTE, {replace: true})}
                        className="btn-secondary min-h-[44px] mt-5"
                        data-testid="calendar-callback-back"
                    >
                        Вернуться к настройкам
                    </button>
                </>
            ) : (
                <>
                    <CalendarIcon size={28} className="mx-auto text-gray-500 dark:text-gray-400"/>
                    <h1 className="mt-3 text-xl font-semibold text-gray-900 dark:text-gray-100">
                        Подключаем Google Calendar…
                    </h1>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        Завершаем обмен с Google. Это займёт пару секунд.
                    </p>
                </>
            )}
        </div>
    );
};

export default CalendarOAuthCallbackPage;
